// ── Polygon helpers for the zone-drawing canvases ─────────────────────────────
// Every drawing surface in the console (seat zones, restricted zones, ignore
// zones, staff zones) joins the clicked points IN CLICK ORDER. Click four corners
// diagonally — top-left, bottom-right, top-right, bottom-left — and the edges
// cross into a bowtie rather than a rectangle.
//
// That is not merely ugly. Every backend membership test is ray-casting
// (_point_in_poly), and on a self-intersecting ring the "inside" of the crossed
// lobes flips, so a zone drawn that way silently tests the wrong area.
//
// Points are normalised [x, y] pairs in 0..1.

const cross = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);

// Do segments p1p2 and p3p4 properly cross? Touching at a shared endpoint does
// not count — consecutive edges of any ring share one by definition.
function segmentsCross(p1, p2, p3, p4) {
  const d1 = cross(p3, p4, p1), d2 = cross(p3, p4, p2);
  const d3 = cross(p1, p2, p3), d4 = cross(p1, p2, p4);
  return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
         ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0));
}

/** True when the closed ring crosses itself (the bowtie). */
export function isSelfIntersecting(poly) {
  const n = (poly || []).length;
  if (n < 4) return false;                       // a triangle cannot cross itself
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      if (i === j) continue;
      if ((i + 1) % n === j || (j + 1) % n === i) continue;   // adjacent edges
      if (segmentsCross(poly[i], poly[(i + 1) % n], poly[j], poly[(j + 1) % n])) return true;
    }
  }
  return false;
}

/**
 * Reorder points into a simple ring by angle about their centroid.
 *
 * This always produces a non-self-intersecting polygon, and for the shapes these
 * canvases are actually used for — a desk, a doorway, a patch of floor — it gives
 * exactly the ring the operator meant. It would round off a deliberately concave
 * outline, so callers should only reach for it once isSelfIntersecting says the
 * ring as clicked is broken, and should say that they changed it.
 */
export function orderRing(poly) {
  const pts = (poly || []).slice();
  if (pts.length < 3) return pts;
  const cx = pts.reduce((a, p) => a + p[0], 0) / pts.length;
  const cy = pts.reduce((a, p) => a + p[1], 0) / pts.length;
  return pts.sort((a, b) => Math.atan2(a[1] - cy, a[0] - cx) - Math.atan2(b[1] - cy, b[0] - cx));
}

/**
 * Ring the operator meant: as clicked when that is already simple, otherwise
 * reordered. Returns { poly, fixed } so the caller can tell them it was changed.
 */
export function makeSimpleRing(poly) {
  if (!isSelfIntersecting(poly)) return { poly, fixed: false };
  const ordered = orderRing(poly);
  return isSelfIntersecting(ordered) ? { poly, fixed: false } : { poly: ordered, fixed: true };
}
